
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { parseDateFromUrl, formatDateForUrl, isValidDate } from '@/utils/dateParser';
import { useDebounce } from '@/hooks/useDebounce';

interface SearchState {
  checkIn: Date | undefined;
  checkOut: Date | undefined;
  guests: string;
}

interface UseSearchStateResult extends SearchState {
  setCheckIn: (date: Date | undefined) => void;
  setCheckOut: (date: Date | undefined) => void;
  setGuests: (guests: string) => void;
  updateFromUrlParams: () => void;
  hasValidDateRange: boolean;
  // Debounced versions for optimized API calls
  debouncedCheckIn: Date | undefined;
  debouncedCheckOut: Date | undefined;
  debouncedGuests: string;
}

export const useSearchState = (
  initialCheckIn?: Date,
  initialCheckOut?: Date,
  initialGuests?: string
): UseSearchStateResult => {
  const [searchParams] = useSearchParams();
  const [checkIn, setCheckIn] = useState<Date | undefined>(initialCheckIn);
  const [checkOut, setCheckOut] = useState<Date | undefined>(initialCheckOut);
  
  // Normalize and validate guests value
  const normalizeGuestsValue = useCallback((value: string | undefined | null) => {
    if (!value || value === '' || value === 'undefined' || value === 'null') {
      return '1';
    }
    
    // Clean any duplicated or invalid text
    const cleanValue = value.toString().replace(/[^\d]/g, '');
    const numericValue = parseInt(cleanValue) || 1;
    
    // Ensure value is within valid range (1-10)
    if (numericValue < 1) return '1';
    if (numericValue > 10) return '10';
    
    return numericValue.toString();
  }, []);

  const [guests, setGuestsInternal] = useState<string>(() => 
    normalizeGuestsValue(initialGuests || '1')
  );

  // Debounced values for API calls (300ms delay)
  const debouncedCheckIn = useDebounce(checkIn, 300);
  const debouncedCheckOut = useDebounce(checkOut, 300);
  const debouncedGuests = useDebounce(guests, 300);

  // Stable setGuests function with validation
  const setGuests = useCallback((newGuests: string) => {
    const normalizedValue = normalizeGuestsValue(newGuests);
    console.log('🔄 useSearchState setting guests:', { input: newGuests, normalized: normalizedValue });
    setGuestsInternal(normalizedValue);
  }, [normalizeGuestsValue]);

  const updateFromUrlParams = useCallback(() => {
    const checkinParam = searchParams.get('checkin');
    const checkoutParam = searchParams.get('checkout');
    const guestsParam = searchParams.get('guests');

    console.log('🔍 Updating search state from URL params:', { 
      checkin: checkinParam, 
      checkout: checkoutParam, 
      guests: guestsParam
    });

    if (checkinParam) {
      const checkinDate = parseDateFromUrl(checkinParam);
      if (checkinDate) {
        setCheckIn(checkinDate);
        console.log('✅ Set check-in from URL:', checkinDate);
      }
    }

    if (checkoutParam) {
      const checkoutDate = parseDateFromUrl(checkoutParam);
      if (checkoutDate) {
        setCheckOut(checkoutDate);
        console.log('✅ Set check-out from URL:', checkoutDate);
      }
    }

    // Always normalize guests value from URL
    const normalizedGuests = normalizeGuestsValue(guestsParam);
    setGuestsInternal(normalizedGuests);
    console.log('✅ Set guests from URL:', { param: guestsParam, normalized: normalizedGuests });
  }, [searchParams, normalizeGuestsValue]);

  // Auto-clear checkout if it's before or equal to checkin
  useEffect(() => {
    if (checkIn && checkOut && checkOut <= checkIn) {
      console.log('📅 Auto-clearing checkout (before checkin)');
      setCheckOut(undefined);
    }
  }, [checkIn, checkOut]);

  const hasValidDateRange = useMemo(() => 
    isValidDate(checkIn) && isValidDate(checkOut) && checkOut! > checkIn!,
    [checkIn, checkOut]
  );

  return {
    checkIn,
    checkOut,
    guests,
    setCheckIn,
    setCheckOut,
    setGuests,
    updateFromUrlParams,
    hasValidDateRange,
    debouncedCheckIn,
    debouncedCheckOut,
    debouncedGuests
  };
};
